/**
 * Route evidence questions to the authenticated grounded-answer service while
 * preserving the existing deterministic workspace command language.
 *
 * This classifier selects a transport, not an evidence intent. The backend is
 * the sole authority that returns why_valid / what_changed / impact / decision
 * intent, including explicit ambiguous and no-match states.
 */
export type LineageWorkspaceInputRoute = "command" | "grounded_question";

const DETERMINISTIC_QUESTION = [
  /\bwhat (?:produced|generated|created) this\b/,
  /\bwhat (?:would|could|will) changing this .+? (?:potentially )?(?:affect|impact|change)\b/,
] as const;

const IMPERATIVE_COMMAND = /^(?:show|hide|find|isolate|clear|reset|compare|focus|open|switch|set|make|move|stress|fit|run|use|load)\b/;
const QUESTION_OPENING = /^(?:why|what|which|how|when|where|who|does|do|did|is|are|was|were|will|would|could|can|should)\b/;
const GROUNDED_INTENT_LANGUAGE = [
  /\bwhy\b.+\bvalid\b/,
  /\bwhat (?:has )?changed\b/,
  /\bwhat (?:becomes?|became|would become|will become) invalid\b/,
  /\b(?:decision|evidence|rationale)\b.+\b(?:supports?|supported|basis|behind|for)\b/,
  /\bwhat\b.+\b(?:decision|evidence|rationale)\b/,
] as const;

export function routeLineageWorkspaceInput(source: string): LineageWorkspaceInputRoute {
  const normalized = source.normalize("NFKC").trim().toLowerCase().replace(/\s+/g, " ");
  if (!normalized) return "command";
  if (/^do something\b/.test(normalized)) return "command";
  if (DETERMINISTIC_QUESTION.some((pattern) => pattern.test(normalized))) return "command";
  if (GROUNDED_INTENT_LANGUAGE.some((pattern) => pattern.test(normalized))) return "grounded_question";
  if (IMPERATIVE_COMMAND.test(normalized)) return "command";
  return normalized.includes("?") || QUESTION_OPENING.test(normalized)
    ? "grounded_question"
    : "command";
}

/** Supply the visible ordered window only for questions that mention change. */
export function groundedQuestionRequestsComparison(source: string): boolean {
  const normalized = source.normalize("NFKC").trim().toLowerCase().replace(/\s+/g, " ");
  return [
    /\bwhat (?:has )?changed\b/,
    /\bchanges? (?:between|since|over time)\b/,
    /\btemporal (?:change|difference|delta)s?\b/,
  ].some((pattern) => pattern.test(normalized));
}
